/**
 * Shuffle algorithms, ported verbatim from V5 with types added.
 *
 *   - `randomShuffle` — Fisher-Yates
 *   - `weightedPermute` — Efraimidis-Spirakis weighted permutation
 *   - `balancedShuffle` — classic round-robin over owner buckets
 *   - `balancedRecencyShuffle` — round-robin with a recency-weighted bucket
 *
 * These are the only pieces of V5 shuffle logic that survived into V6
 * unchanged. Pure functions; unit-tested in `shuffle.test.ts`.
 */
import type { Track } from "@memes/shared";

/** Uniform [0, 1) using crypto.getRandomValues instead of Math.random. */
function secureRandom(): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return buf[0]! / 4294967296;
}

export function randomShuffle<T>(arr: readonly T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(secureRandom() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/**
 * Efraimidis-Spirakis weighted permutation. Items with higher weight are
 * more likely to appear earlier in the output.
 */
export function weightedPermute<T>(items: readonly T[], weight: (x: T) => number): T[] {
  const keyed = items.map((x) => {
    const w = Math.max(0.0001, weight(x));
    const u = secureRandom();
    const k = Math.pow(u, 1 / w);
    return { x, k };
  });
  keyed.sort((a, b) => b.k - a.k);
  return keyed.map((k) => k.x);
}

/** Owners that have tracks, in a random round-robin order (varies each shuffle). */
function shuffledOwnerOrder(
  buckets: Map<string, Track[]>,
  owners: readonly string[],
): string[] {
  const withTracks = owners.filter((o) => (buckets.get(o)?.length ?? 0) > 0);
  const extras = [...buckets.keys()].filter((o) => !owners.includes(o));
  return randomShuffle([...withTracks, ...extras]);
}

/** Owner order for one round; avoids back-to-back same owner when possible. */
function ownerOrderForRound(
  buckets: Map<string, Track[]>,
  owners: readonly string[],
  lastOwner: string | null,
): string[] {
  const order = shuffledOwnerOrder(buckets, owners);
  const nonEmpty = order.filter((o) => (buckets.get(o)?.length ?? 0) > 0);
  if (nonEmpty.length <= 1 || lastOwner == null) return order;

  const firstEmitter = order.find((o) => (buckets.get(o)?.length ?? 0) > 0)!;
  if (firstEmitter !== lastOwner) return order;

  const alternate = order.find(
    (o) => o !== firstEmitter && (buckets.get(o)?.length ?? 0) > 0,
  );
  if (alternate == null) return order;

  const i = order.indexOf(firstEmitter);
  const j = order.indexOf(alternate);
  [order[i], order[j]] = [order[j]!, order[i]!];
  return order;
}

/** Group tracks by owner, preserving insertion order. */
function groupByOwner(tracks: readonly Track[]): Map<string, Track[]> {
  const buckets = new Map<string, Track[]>();
  for (const t of tracks) {
    const existing = buckets.get(t.owner);
    if (existing) existing.push(t);
    else buckets.set(t.owner, [t]);
  }
  return buckets;
}

/**
 * Round-robin shuffle over per-owner buckets. Each bucket is itself
 * shuffled, then we emit one track per bucket in rotating order until all
 * buckets are empty.
 */
export function balancedShuffle(tracks: readonly Track[], owners: readonly string[]): Track[] {
  const buckets = groupByOwner(tracks);
  for (const owner of buckets.keys()) {
    buckets.set(owner, randomShuffle(buckets.get(owner)!));
  }
  const result: Track[] = [];
  let still = true;
  let lastOwner: string | null = null;
  while (still) {
    still = false;
    const order = ownerOrderForRound(buckets, owners, lastOwner);
    for (const owner of order) {
      const b = buckets.get(owner);
      if (b && b.length > 0) {
        result.push(b.shift()!);
        lastOwner = owner;
        still = true;
      }
    }
  }
  return result;
}

/**
 * Recency-aware bucket shuffle. Items that haven't been played in a while
 * get a higher weight, so they're more likely to surface first within
 * each bucket. Round-robin over buckets still applies.
 *
 * `newerW` and `olderW` are weights applied to the extremes of the
 * recency spectrum; typical values: newerW=0.5, olderW=2.0.
 */
export function balancedRecencyShuffle(
  tracks: readonly Track[],
  owners: readonly string[],
  newerW: number,
  olderW: number,
): Track[] {
  const buckets = groupByOwner(tracks);
  const now = Math.floor(Date.now() / 1000);
  const shuffleSalt = `${Date.now()}-${crypto.randomUUID()}`;
  for (const owner of buckets.keys()) {
    const items = buckets.get(owner)!;
    const maxAge = Math.max(
      1,
      Math.max(...items.map((t) => (t.lastPlayed == null ? now : now - t.lastPlayed))),
    );
    const permuted = weightedPermute(items, (t) => {
      let w: number;
      if (t.lastPlayed == null) {
        w = olderW;
      } else {
        const age = now - t.lastPlayed;
        // Linearly interpolate between newerW (age=0) and olderW (age=maxAge).
        const ratio = Math.min(1, age / maxAge);
        w = newerW + (olderW - newerW) * ratio;
      }
      // Per-shuffle salt so two shuffles in the same second still differ.
      let h = 0;
      const key = `${shuffleSalt}:${t.id}`;
      for (let i = 0; i < key.length; i++) {
        h = Math.imul(31, h) + key.charCodeAt(i)!;
      }
      return w * (0.9 + ((h >>> 0) % 200) / 1000);
    });
    buckets.set(owner, permuted);
  }
  const result: Track[] = [];
  let still = true;
  let lastOwner: string | null = null;
  while (still) {
    still = false;
    const order = ownerOrderForRound(buckets, owners, lastOwner);
    for (const owner of order) {
      const b = buckets.get(owner);
      if (b && b.length > 0) {
        result.push(b.shift()!);
        lastOwner = owner;
        still = true;
      }
    }
  }
  return result;
}
